import React, { useState } from 'react';

import { Alert, Box, Button, Divider, LinearProgress, Stack, Typography } from '@mui/material';

import {
  type LensDatasetCardDiff as DatasetCardDiff,
  useDryRunReportingLeLensDataset,
  useMergeCheckReportingLeLensDataset,
} from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type { LensCard, LensDataset } from '~/types/leLens';

import { bestDatasetSql, describeRejection } from '../../buildPlan';

import { extendColumnPrompt, extractCannotLine } from './extendPrompt';
import { blockedCardReason, describeCardChange, mergeBannerLine } from './guardCopy';
import { streamDatasetSqlAnswer } from './streamAiSql';

/**
 * F7: the merge banner. Shown in the dataset block (`DatasetFacts.tsx`'s
 * `mergeBanner` slot) when `mergeCandidates.ts` finds two datasets on the
 * board reading the same table at the same grain.
 *
 * merge-check has two modes (`checks.ts`). Preview (no `sql`) shapes `from`'s
 * cards against `into`'s current, unmodified rows and names `unionRequired`,
 * the `from` columns `into` lacks. That is only informative when `into`
 * already carries everything `from`'s cards need; Bedbank's one-card
 * datasets never do, since no two of them share a full column set. When
 * `unionRequired` is non-empty, this asks AskRichard for a union rewrite of
 * `into`'s query with the same extend-only brief and `runCardStream` path
 * Add column uses (`AddColumn.tsx`), then re-runs merge-check with that
 * candidate as `sql` (union mode): `columnsMissing` (refuse if the rewrite
 * still is not a true superset), `grainKept` (refuse if `into`'s declared
 * grain no longer holds), and `cardDiffs` for the cards of *both* datasets.
 *
 * A `from` card that comes back non-identical is blocked, not moved: it
 * keeps reading `from`, which stays on the board for it, F7's own "kept on
 * its own dataset because its number would change." An `into` card coming
 * back non-identical is a different problem: it already lives on `into`,
 * so there is no dataset to leave it on. Swapping in a union rewrite that
 * changes what `into`'s own cards show would violate the whole premise of a
 * merge (one query, unaffected numbers), so any non-identical `into` card
 * refuses the merge outright rather than picking a per-card outcome for a
 * dataset that has no "somewhere else" to fall back to.
 */

type Stage =
  | { kind: 'idle' }
  | { kind: 'previewing' }
  | { kind: 'unioning' }
  | {
      kind: 'previewed';
      moving: Array<{ card: LensCard; diff: DatasetCardDiff }>;
      blocked: Array<{ card: LensCard; diff: DatasetCardDiff }>;
      /** `into` unchanged (no union needed), or `into` rewritten with the union candidate's
       *  sql/columns/dryRun (its grain is unchanged either way): what Merge actually saves. */
      survivor: LensDataset;
      /** Set only when a union rewrite ran, for the "queries before and after" block. */
      queryBefore?: string;
      queryAfter?: string;
    }
  | { kind: 'refused'; reasonLine: string; details: Array<{ label: string; value: string }> }
  | { kind: 'failed'; message: string }
  | { kind: 'merging' };

export type MergeBannerProps = {
  lensId: string;
  from: LensDataset;
  into: LensDataset;
  table: string;
  grainKeys: ReadonlyArray<string>;
  /** Every card on the board; only `from`'s and `into`'s are relevant to the preview and the move. */
  cards: ReadonlyArray<LensCard>;
  /** Removes `from` from the board and repoints `movedCardIds` at `survivor` (a card not in
   *  `movedCardIds`, a blocked card, keeps reading `from`, which stays on the board unmerged for
   *  it). `survivor` carries the merged SQL, columns and grain when a union ran. */
  onMerge: (survivor: LensDataset, absorbedDatasetId: string, movedCardIds: ReadonlyArray<string>) => Promise<void>;
  onDismiss: () => void;
};

export default function MergeBanner({
  cards,
  from,
  grainKeys,
  into,
  lensId,
  onDismiss,
  onMerge,
  table,
}: MergeBannerProps) {
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const mergeCheck = useMergeCheckReportingLeLensDataset();
  const dryRun = useDryRunReportingLeLensDataset();
  const fromCards = cards.filter((card) => card.datasetId === from.id);
  const intoCards = cards.filter((card) => card.datasetId === into.id);

  const partition = (diffs: Array<DatasetCardDiff>, ownCards: Array<LensCard>) => {
    const byId = new Map(diffs.map((diff) => [diff.cardId, diff]));
    const moving: Array<{ card: LensCard; diff: DatasetCardDiff }> = [];
    const blocked: Array<{ card: LensCard; diff: DatasetCardDiff }> = [];
    ownCards.forEach((card) => {
      const diff = byId.get(card.id);
      if (!diff) {
        return;
      }
      (diff.identical ? moving : blocked).push({ card, diff });
    });
    return { blocked, moving };
  };

  const preview = async () => {
    setStage({ kind: 'previewing' });
    try {
      const first = await mergeCheck.mutateAsync({ from: from.id, into: into.id, lensId });

      if (!first.unionRequired?.length) {
        if (!first.grainKept) {
          setStage({ details: [], kind: 'refused', reasonLine: `This would change ${into.name}'s grain.` });
          return;
        }
        const { blocked, moving } = partition(first.cardDiffs, fromCards);
        setStage({ blocked, kind: 'previewed', moving, survivor: into });
        return;
      }

      setStage({ kind: 'unioning' });
      const keepColumns = (into.columns ?? []).map((column) => column.name);
      const requiredColumns = [...keepColumns, ...first.unionRequired];
      const grainLine = into.grain?.keys?.length ? into.grain.keys.join(' × ') : 'unchanged';
      const request = `Also return ${first.unionRequired.join(', ')}, defined the same way ${from.name} does. Keep the result at the same grain as today (${grainLine}).`;
      const { answer, sqlBlocks, streamError } = await streamDatasetSqlAnswer(
        extendColumnPrompt({ columns: keepColumns, name: into.name, sql: into.sql }, request),
      );
      if (streamError) {
        setStage({ kind: 'failed', message: streamError });
        return;
      }
      const cannot = extractCannotLine(answer);
      const candidateSql = bestDatasetSql(sqlBlocks, requiredColumns);
      if (!candidateSql) {
        setStage({ kind: 'failed', message: cannot ?? describeRejection(sqlBlocks, requiredColumns) });
        return;
      }

      let dryRunResult: { columns: LensDataset['columns']; bytesProcessed: number; referencedTables: Array<string> };
      try {
        dryRunResult = await dryRun.mutateAsync(candidateSql);
      } catch (error) {
        setStage({ kind: 'failed', message: error instanceof Error ? error.message : String(error) });
        return;
      }

      let second: Awaited<ReturnType<typeof mergeCheck.mutateAsync>>;
      try {
        second = await mergeCheck.mutateAsync({ from: from.id, into: into.id, lensId, sql: candidateSql });
      } catch (error) {
        setStage({ kind: 'failed', message: error instanceof Error ? error.message : String(error) });
        return;
      }

      if (second.columnsMissing?.length) {
        setStage({
          details: [],
          kind: 'refused',
          reasonLine: `The rewritten query is still missing ${second.columnsMissing.join(', ')}.`,
        });
        return;
      }
      if (!second.grainKept) {
        setStage({ details: [], kind: 'refused', reasonLine: `This would change ${into.name}'s grain.` });
        return;
      }

      const intoDiffs = second.cardDiffs.filter((diff) => intoCards.some((card) => card.id === diff.cardId));
      const nonIdenticalInto = intoDiffs.filter((diff) => !diff.identical);
      if (nonIdenticalInto.length) {
        const cardById = new Map(intoCards.map((card) => [card.id, card]));
        setStage({
          details: nonIdenticalInto.map((diff) => ({
            label: cardById.get(diff.cardId)?.title ?? diff.cardId,
            value: describeCardChange(diff, cardById.get(diff.cardId), into),
          })),
          kind: 'refused',
          reasonLine: `This would change what ${into.name}'s own cards show.`,
        });
        return;
      }

      const fromDiffs = second.cardDiffs.filter((diff) => fromCards.some((card) => card.id === diff.cardId));
      const { blocked, moving } = partition(fromDiffs, fromCards);
      const survivor: LensDataset = {
        ...into,
        columns: second.columnsAfter ?? into.columns,
        dryRun: {
          at: new Date().toISOString(),
          bytesProcessed: second.bytesProcessed,
          referencedTables: dryRunResult.referencedTables,
        },
        sql: candidateSql,
      };
      setStage({ blocked, kind: 'previewed', moving, queryAfter: candidateSql, queryBefore: into.sql, survivor });
    } catch (error) {
      setStage({ kind: 'failed', message: error instanceof Error ? error.message : String(error) });
    }
  };

  const merge = async () => {
    if (stage.kind !== 'previewed') {
      return;
    }
    setStage({ kind: 'merging' });
    try {
      await onMerge(
        stage.survivor,
        from.id,
        stage.moving.map((entry) => entry.card.id),
      );
    } catch (error) {
      setStage({ kind: 'failed', message: error instanceof Error ? error.message : String(error) });
    }
  };

  return (
    <Alert
      action={
        stage.kind === 'idle' || stage.kind === 'failed' || stage.kind === 'refused' ? (
          <Stack direction="row" spacing={1}>
            <Button onClick={preview} size="small" variant="contained">
              Preview
            </Button>
            <Button onClick={onDismiss} size="small">
              Not now
            </Button>
          </Stack>
        ) : undefined
      }
      severity="info"
      variant="outlined"
    >
      <Typography variant="body2">{mergeBannerLine(from, into, table, grainKeys)}</Typography>

      {(stage.kind === 'previewing' || stage.kind === 'unioning') && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Typography color="text.secondary" variant="caption">
            {stage.kind === 'previewing'
              ? 'Checking whether the two datasets can merge…'
              : 'Asking askRichard for a query that returns both…'}
          </Typography>
          <LinearProgress />
        </Stack>
      )}

      {stage.kind === 'failed' && (
        <Typography color="error" sx={{ display: 'block', mt: 0.5 }} variant="caption">
          {stage.message}
        </Typography>
      )}

      {stage.kind === 'refused' && (
        <Box sx={{ mt: 1 }}>
          <Typography color="error" sx={{ fontWeight: 500 }} variant="body2">
            {stage.reasonLine}
          </Typography>
          {stage.details.length > 0 && (
            <Box component="table" sx={{ borderCollapse: 'collapse', mt: 0.75, width: '100%' }}>
              <tbody>
                {stage.details.map((detail) => (
                  <tr key={detail.label}>
                    <Box component="td" sx={{ fontSize: 13, py: 0.25 }}>
                      {detail.label}
                    </Box>
                    <Box component="td" sx={{ fontFamily: 'monospace', fontSize: 12.5, py: 0.25, textAlign: 'right' }}>
                      {detail.value}
                    </Box>
                  </tr>
                ))}
              </tbody>
            </Box>
          )}
        </Box>
      )}

      {stage.kind === 'previewed' && (
        <Box sx={{ mt: 1 }}>
          <Stack spacing={0.25} sx={{ fontSize: 12.5 }}>
            <Row
              label="Cards that move"
              value={`${stage.moving.length} · ${stage.moving.map((entry) => entry.card.title).join(', ') || 'none'}`}
            />
            <Row label="Columns after merge" value={`${stage.survivor.columns?.length ?? 0}`} />
            <Row label="Queries per load" value="2 to 1" />
            {stage.blocked.map((entry) => (
              <Row key={entry.card.id} label={entry.card.title} value={blockedCardReason()} />
            ))}
          </Stack>

          {stage.queryBefore !== undefined && stage.queryAfter !== undefined && (
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ mb: 0.75 }} />
              <Typography color="text.secondary" variant="caption">
                Query before
              </Typography>
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  m: 0,
                  mb: 0.75,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {stage.queryBefore}
              </Box>
              <Typography color="text.secondary" variant="caption">
                Query after
              </Typography>
              <Box
                component="pre"
                sx={{ fontFamily: 'monospace', fontSize: 11, m: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}
              >
                {stage.queryAfter}
              </Box>
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Button onClick={merge} size="small" variant="contained">
              Merge
            </Button>
            <Button onClick={onDismiss} size="small">
              Not now
            </Button>
          </Stack>
        </Box>
      )}

      {stage.kind === 'merging' && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress />
        </Box>
      )}
    </Alert>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="caption">{label}</Typography>
      <Typography sx={{ fontFamily: 'monospace' }} variant="caption">
        {value}
      </Typography>
    </Stack>
  );
}
